<template>
  <v-tooltip
    max-width="400"
    top
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="success"
        @click="getRelatorio()"
        v-on="on"
      >
        <v-icon>get_app</v-icon>
        BAIXAR PDF
      </v-btn>
    </template>
    <span>Clique para baixar o relatório dos dados em formato PDF.</span>
  </v-tooltip>
</template>

<script>
import _ from 'lodash';
import Data from '@/modules/data';
import { generatePDF, getCanvas } from '@/modules/relatorioPDF';

export default {
  name: 'BotaoPDF',

  props: {
    relatorios: {
      type: Array,
      required: true,
    },
    diagnostico: {
      type: Object,
      required: true,
    },
    unidadesVisualizacao: {
      type: String,
      required: true,
    },
    perspectiva: {
      type: String,
      required: true,
    },
  },

  computed: {
    dadosTabelaQuadroResumoPDF() {
      return this.relatorios.map((r) => {
        const totais = this.perspectiva === 'COMPORTAMENTAL' ? r.totaisComportamental : r.totaisTecnico;
        const totalParticipantes = totais.totalConcluidos
          + totais.totalPendentes;
        const pctConclusao = this.calcularPct(totais.totalConcluidos, totalParticipantes);

        return [
          r.unidade.nome,
          r.unidade.sigla,
          r.servidores.length,
          totais.totalDispensados,
          totais.totalPendentes,
          totais.totalConcluidos,
          this.calcularPctStr(pctConclusao),
        ];
      });
    },
    totaisTabelaQuadroResumoPDF() {
      const totalConcluidos = _(this.dadosTabelaQuadroResumoPDF).sumBy((i) => i[5]); // totalConcluidos
      const totalPendentes = _(this.dadosTabelaQuadroResumoPDF).sumBy((i) => i[4]); // totalPendentes
      const totalParticipantes = totalConcluidos + totalPendentes;
      const pctConclusao = this.calcularPct(totalConcluidos, totalParticipantes);

      return [
        '',
        'TOTAL',
        _(this.dadosTabelaQuadroResumoPDF).sumBy((i) => i[2]), // totalServidores
        _(this.dadosTabelaQuadroResumoPDF).sumBy((i) => i[3]), // totalDispensados
        totalPendentes,
        totalConcluidos,
        this.calcularPctStr(pctConclusao),
      ];
    },
    dadosTabelaResultadosPDF() {
      if (this.perspectiva === 'COMPORTAMENTAL') {
        return _.flatMap(this.relatorios, (r) => r.servidores.filter((s) => !s.foiDispensado)
          .map((s) => [
            s.usuario.nome,
            r.unidade.sigla,
            s.usuario.categoria,
            s.fezAutoavaliacao ? 'Sim' : 'Não',
            s.fezAvaliacaoGestor !== null ? (s.fezAvaliacaoGestor ? 'Sim' : 'Não') : 'Não se aplica',
            (s.avaliaGestores || s.avaliaServidores || s.avaliaPares)
              ? (s.fezAvaliacaoEquipe ? 'Sim' : 'Não')
              : 'Não se aplica',
            s.situacao.descricao,
          ]));
      }

      if (this.perspectiva === 'TECNICA') {
        return _.flatMap(this.relatorios, (r) => r.servidores.filter((s) => !s.foiDispensado)
          .map((s) => [
            s.usuario.nome,
            r.unidade.sigla,
            s.usuario.categoria,
            s.temResponsabilidadeASerAvaliado
              ? (s.fezAutoavaliacaoTecnica ? 'Sim' : 'Não')
              : 'Não há responsabilidades a serem avaliadas',
            s.avaliaServidoresTecnico ? (s.fezAvaliacaoTecnicaEquipeServidores ? 'Sim' : 'Não') : 'Não se aplica',
            s.avaliaGestores ? (s.fezAvaliacaoTecnicaEquipeGestores ? 'Sim' : 'Não') : 'Não se aplica',
            s.situacao.descricao,
          ]));
      }

      return 0;
    },
    dadosTabelaDispensadosPDF() {
      return _.flatMap(this.relatorios, (r) => r.servidores.filter((s) => s.foiDispensado)
        .map((s) => [
          s.usuario.nome,
          r.unidade.sigla,
          s.usuario.categoria,
          s.motivoDispensado,
        ]));
    },
  },

  methods: {
    periodoDiagnostico() {
      if (this.diagnostico) {
        let inicio;
        let fim;
        const { inicioComportamental } = this.diagnostico;
        const inicioTecnico = this.diagnostico.inicioResponsabilidadeChefia;
        const { fimComportamental } = this.diagnostico;
        const fimTecnico = this.diagnostico.inicioResponsabilidadeServidor;

        if (Data.compare('isBefore', { a: inicioComportamental, b: inicioTecnico })) {
          inicio = Data.format(inicioComportamental);
        } else {
          inicio = Data.format(inicioTecnico);
        }

        if (Data.compare('isAfter', { a: fimComportamental, b: fimTecnico })) {
          fim = Data.format(fimComportamental);
        } else {
          fim = Data.format(fimTecnico);
        }

        return `${inicio} a ${fim}`;
      }
      return '';
    },
    calcularPct(nume, deno) {
      if (deno > 0) {
        return nume / deno;
      }
      return null;
    },
    calcularPctStr(pct) {
      if (pct !== null) {
        pct *= 100;
        return `${pct.toLocaleString(undefined, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })}%`;
      }
      return `${0}%`;
    },
    async getRelatorio() {
      const documento = {
        nome: `relatorio-monitoramento-diagnostico-${
          this.perspectiva === 'COMPORTAMENTAL' ? 'comportamental' : 'tecnico'
        }`,
        orientacao: 'landscape',
        cabecalho: {
          titulo: `Relatório do Diagnóstico ${this.perspectiva === 'COMPORTAMENTAL' ? 'Comportamental' : 'Técnico'}`,
          itens: [
            `Diagnóstico: ${this.diagnostico.nome}`,
            `Unidades: ${this.unidadesVisualizacao.slice(1, -1)}`,
            `Período: ${this.periodoDiagnostico()}`,
          ],
        },
        blocos: [
          {
            titulo: 'GRÁFICOS',
            subtitulo: '',
            itens: [
              { tipo: 'imagem', valor: await getCanvas('#grafico-gauge'), altura: 100 },
            ],
          },
          {
            titulo: `QUADRO RESUMO DO DIAGNÓSTICO ${this.perspectiva}`,
            subtitulo: '',
            itens: [{
              tipo: 'tabela',
              valor: {
                colunas: [
                  'NOME DA UNIDADE',
                  'SIGLA DA UNIDADE',
                  'TOTAL DE SERVIDORES',
                  'DISPENSADOS',
                  'PENDENTES',
                  'CONCLUÍDOS',
                  'PERCENTUAL DE CONCLUSÃO DA UNIDADE',
                ],
                linhas: this.dadosTabelaQuadroResumoPDF,
                rodape: this.totaisTabelaQuadroResumoPDF,
              },
            }],
          },
          {
            titulo: 'RESULTADOS',
            subtitulo: '',
            itens: [{
              tipo: 'tabela',
              valor: {
                colunas: [
                  'SERVIDOR',
                  'UNIDADE',
                  'CATEGORIA',
                  'AUTOAVALIAÇÃO',
                  this.perspectiva === 'COMPORTAMENTAL' ? 'AVALIAÇÃO DO GESTOR' : 'AVALIAÇÃO DA EQUIPE DE SERVIDORES',
                  this.perspectiva === 'COMPORTAMENTAL' ? 'AVALIAÇÃO DA EQUIPE' : 'AVALIAÇÃO DA EQUIPE DE GESTORES',
                  'SITUAÇÃO',
                ],
                linhas: this.dadosTabelaResultadosPDF,
              },
            }],
          },
          this.dadosTabelaDispensadosPDF.length > 0 ? {
            titulo: 'SERVIDORES DISPENSADOS',
            subtitulo: '',
            itens: [{
              tipo: 'tabela',
              valor: {
                colunas: [
                  'SERVIDOR',
                  'UNIDADE',
                  'CATEGORIA',
                  'MOTIVO',
                ],
                linhas: this.dadosTabelaDispensadosPDF,
              },
            }],
          } : {},
        ],
      };
      generatePDF(documento);
    },
  },
};
</script>
